import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faEdit, faEye } from '@fortawesome/free-solid-svg-icons';
import CreditManagementApi from '@src/apis/creditManagement';
import { COLOR } from '@src/styles/color';
import { useDebounce } from '@src/hooks';
import { StyledDetailCreditModal } from './modal.styles';

const DetailCreditModal = ({ open, creditPackage, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    toolId: '',
    displayName: '',
    creditValue: '',
    isActive: true,
  });

  const [tools, setTools] = useState([]);
  const [searchTool, setSearchTool] = useState('');
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);

  const debouncedSearchTool = useDebounce(searchTool, 500);

  const populateFormData = () => {
    if (creditPackage) {
      setFormData({
        toolId: creditPackage.tool?.id || '',
        displayName: creditPackage.displayName || '',
        creditValue: creditPackage.creditValue || '',
        isActive: creditPackage.isActive || false,
      });

      const toolName =
        creditPackage.tool?.translations?.vi?.name ||
        creditPackage.tool?.translations?.en?.name ||
        creditPackage.tool?.name?.vi ||
        creditPackage.tool?.name?.en ||
        '';
      setSearchTool(toolName);
    }
  };

  const fetchTools = async () => {
    try {
      setLoadingTools(true);
      const params = {
        searchTerm: debouncedSearchTool,
        page: 1,
        limit: 10,
      };
      const response = await CreditManagementApi.searchTools(params);
      if (response?.items) {
        setTools(response.items || []);
        setShowToolsDropdown(true);
      }
    } finally {
      setLoadingTools(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleToolSearch = (value) => {
    setSearchTool(value);
    if (value === '') {
      setFormData((prev) => ({
        ...prev,
        toolId: '',
      }));
    }
  };

  const handleToolFocus = () => {
    setShowToolsDropdown(true);
    fetchTools();
  };

  const handleToolBlur = () => {
    setTimeout(() => {
      setShowToolsDropdown(false);
    }, 200);
  };

  const handleSelectTool = (tool) => {
    setFormData((prev) => ({
      ...prev,
      toolId: tool.id,
    }));
    setSearchTool(
      tool.translations?.vi?.name ||
        tool.translations?.en?.name ||
        tool.name?.vi ||
        tool.name?.en ||
        '',
    );
    setShowToolsDropdown(false);
  };

  const handleStatusChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      isActive: checked,
    }));
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      populateFormData();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.toolId) {
      newErrors.toolId = t('toolRequired');
    }
    if (!formData.displayName?.trim()) {
      newErrors.displayName = t('packageNameRequired');
    }
    if (!formData.creditValue || formData.creditValue <= 0) {
      newErrors.creditValue = t('creditValueRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await CreditManagementApi.updateCreditPackage(
        creditPackage.id,
        formData,
      );

      if (response?.status) {
        enqueueSnackbar(t('updateCreditPackageSuccess'), {
          variant: 'success',
        });
        setIsEditing(false);
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(t('updateCreditPackageError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      setIsEditing(false);
      populateFormData();
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (creditPackage) {
      populateFormData();
    }
  }, [creditPackage]);

  useEffect(() => {
    if (debouncedSearchTool.length >= 1 && showToolsDropdown) {
      fetchTools();
    }
  }, [debouncedSearchTool]);

  const renderField = (label, value, isMultiline = false) => (
    <Box className={isMultiline ? 'view-content-multiline' : 'view-content'}>
      <Typography variant="body1" className="view-text">
        {value || '-'}
      </Typography>
    </Box>
  );

  return (
    <StyledDetailCreditModal open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle className="dialog-title">
        {t('creditPackageDetail')}
        <Box className="mode-toggle">
          <Button
            variant={isEditing ? 'outlined' : 'contained'}
            size="small"
            onClick={handleEditToggle}
            startIcon={
              <FontAwesomeIcon icon={isEditing ? faEye : faEdit} size="sm" />
            }
          >
            {isEditing ? t('viewMode') : t('editMode')}
          </Button>
          <IconButton onClick={onClose}>
            <FontAwesomeIcon
              icon={faXmark}
              color={COLOR.text.highlight}
              size="sm"
            />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className="dialog-content">
        <Box className="form-section">
          <Typography variant="h6" className="section-title">
            {t('basicInformation')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">
                {t('packageName')} <span className="required">*</span>
              </Typography>
              {isEditing ? (
                <TextField
                  size="small"
                  fullWidth
                  value={formData.displayName}
                  onChange={(e) =>
                    handleInputChange('displayName', e.target.value)
                  }
                  error={!!errors.displayName}
                  helperText={errors.displayName}
                  placeholder={t('enterPackageName')}
                />
              ) : (
                renderField(t('packageName'), formData.displayName)
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">
                {t('creditValue')} <span className="required">*</span>
              </Typography>
              {isEditing ? (
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  value={formData.creditValue}
                  onChange={(e) =>
                    handleInputChange(
                      'creditValue',
                      parseInt(e.target.value, 10) || 0,
                    )
                  }
                  error={!!errors.creditValue}
                  helperText={errors.creditValue}
                  placeholder={t('enterCreditValue')}
                />
              ) : (
                renderField(t('creditValue'), formData.creditValue)
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography className="field-label">
                {t('selectTool')} <span className="required">*</span>
              </Typography>
              {isEditing ? (
                <Box className="tool-search-container">
                  <TextField
                    size="small"
                    fullWidth
                    value={searchTool}
                    onChange={(e) => handleToolSearch(e.target.value)}
                    onFocus={handleToolFocus}
                    onBlur={handleToolBlur}
                    error={!!errors.toolId}
                    helperText={errors.toolId}
                    placeholder={t('searchTools')}
                  />
                  {showToolsDropdown && (
                    <Box className="tool-dropdown">
                      {loadingTools && (
                        <Box p={2} textAlign="center">
                          {t('loading')}...
                        </Box>
                      )}
                      {!loadingTools && tools.length > 0 && (
                        <>
                          {tools.map((tool) => {
                            let toolName = '';
                            if (tool.translations?.vi?.name) {
                              toolName = tool.translations.vi.name;
                            } else if (tool.translations?.en?.name) {
                              toolName = tool.translations.en.name;
                            } else if (tool.name?.vi) {
                              toolName = tool.name.vi;
                            } else if (tool.name?.en) {
                              toolName = tool.name.en;
                            } else {
                              toolName = tool.id;
                            }
                            return (
                              <Box
                                key={tool.id}
                                className="tool-option"
                                onClick={() => handleSelectTool(tool)}
                              >
                                <Typography>{toolName}</Typography>
                              </Box>
                            );
                          })}
                        </>
                      )}
                      {!loadingTools && tools.length === 0 && (
                        <Box p={2} textAlign="center" color="text.secondary">
                          {t('noDataFound')}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                renderField(t('toolName'), searchTool)
              )}
            </Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="section-title">
            {t('settings')}
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleStatusChange(e.target.checked)}
                  disabled={!isEditing}
                />
              }
              label={t('isActive')}
            />
          </FormGroup>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} variant="text">
          {isEditing ? t('cancel') : t('close')}
        </Button>
        {isEditing && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? t('updating') : t('update')}
          </Button>
        )}
      </DialogActions>
    </StyledDetailCreditModal>
  );
};

export default DetailCreditModal;
